import { ChangeEvent, useMemo, useState } from 'react'
import { translate } from '../../../../../I18n/i18n'
import { useTranslation } from 'react-i18next'
import { ShareOptionsUtils } from '../../../../../utils/map-utils'
import { ShareCenterOption, ShareResidentOption, ShareFamiliarOption } from '../../../../model/domain/share-option'
import { Spinner } from '@cuatroochenta/co-admin-react-library'
import './AdvancedShareWithCentersSelector.scss'

enum CheckType {
    total, partial
}

interface AdvancedShareWithCentersSelectorProps {
    loading: boolean
    shareOptions?: ShareCenterOption[]
    onCancel: () => void
    onAccept: () => void
    onSelection: (selection?: ShareCenterOption[]) => void
}

export default function AdvancedShareWithCentersSelector({
    loading,
    shareOptions,
    onCancel,
    onAccept,
    onSelection
}: AdvancedShareWithCentersSelectorProps) {
    const [searchQuery, setSearchQuery] = useState('')
    const [expandedCenterId, setExpandedCenterId] = useState<ShareCenterOption['id'] | null>(null)
    const [expandedResidentId, setExpandedResidentId] = useState<ShareResidentOption['id'] | null>(null)
    const { t } = useTranslation()

    const filteredCenters = useMemo(() => {
        return shareOptions?.filter(centerShareOption =>
            centerShareOption.name.toLowerCase().includes(searchQuery.toLowerCase())
        )
    }, [shareOptions, searchQuery])

    const filteredResidents = useMemo(() => {
        const expandedCenter = shareOptions?.find(center => center.id === expandedCenterId)
        return expandedCenter?.suboptions?.filter(residentShareOption =>
            residentShareOption.name.toLowerCase().includes(searchQuery.toLowerCase())
        )
    }, [shareOptions, expandedCenterId, searchQuery])

    const filteredFamiliars = useMemo(() => {
        const expandedCenter = shareOptions?.find(center => center.id === expandedCenterId)
        const expandedResident = expandedCenter?.suboptions?.find(resident => resident.id === expandedResidentId)
        return expandedResident?.suboptions?.filter(familiarShareOption =>
            familiarShareOption.name.toLowerCase().includes(searchQuery.toLowerCase())
        )
    }, [shareOptions, expandedCenterId, expandedResidentId, searchQuery])

    const getCenterCheckType = (option: ShareCenterOption): CheckType | null => {
        if (option.checked) {
            return CheckType.total
        }

        if (option.suboptions?.some(resident =>
            resident.checked ||
            resident.suboptions?.some(familiar => familiar.checked)
        )) {
            return CheckType.partial
        }

        return null
    }

    const getResidentCheckType = (option: ShareResidentOption): CheckType | null => {
        if (option.checked) {
            return CheckType.total
        }

        if (option.suboptions?.some(familiar => familiar.checked)) {
            return CheckType.partial
        }

        return null
    }

    const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
        setSearchQuery(event.target.value)
    }

    const markCenters = () => {
        const allChecked = filteredCenters?.every(center => center.checked)
        const allCentersSelection = shareOptions?.map(centerShareOption => {
            if (filteredCenters?.some(center => center.id === centerShareOption.id)) {
                return ShareOptionsUtils.copyCenterRecursive(centerShareOption, { checked: !allChecked })
            }
            return centerShareOption
        })

        onSelection(allCentersSelection)
    }

    const markResidents = () => {
        const expandedCenter = shareOptions?.find(center => center.id === expandedCenterId)
        if (expandedCenter?.checked) {
            return
        }

        const allChecked = filteredResidents?.every(resident => resident.checked)
        const allResidentsSelection = filteredResidents?.map(residentShareOption => ({
            ...residentShareOption,
            checked: !allChecked,
        })) ?? []

        const newSelection = shareOptions?.map(center => {
            if (center.id === expandedCenterId) {
                return {
                    ...center,
                    suboptions: center.suboptions?.map(resident => {
                        const markedResident = allResidentsSelection.find(selectionResident => selectionResident.id === resident.id)
                        return {
                            ...markedResident ?? resident,
                            suboptions: (markedResident ?? resident).suboptions?.map(familiar => ({
                                ...familiar,
                                checked: !allChecked
                            }))
                        }
                    })
                }
            }

            return center
        })

        onSelection(newSelection)
    }

    const markFamiliars = () => {
        const expandedCenter = shareOptions?.find(center => center.id === expandedCenterId)
        const expandedResident = expandedCenter?.suboptions?.find(
            resident => resident.id === expandedResidentId
        )
        if (expandedResident?.checked) {
            return
        }

        const allChecked = filteredFamiliars?.every(familiar => familiar.checked)
        const allFamiliarsSelection = filteredFamiliars?.map(residentShareOption => ({
            ...residentShareOption,
            checked: !allChecked
        })) ?? []

        const newSelection = shareOptions?.map(center => {
            if (center.id === expandedCenterId) {
                return {
                    ...center,
                    suboptions: center.suboptions?.map(resident => {
                        if (resident.id === expandedResidentId) {
                            return {
                                ...resident,
                                suboptions: resident.suboptions?.map(familiar => {
                                    const markedFamiliar = allFamiliarsSelection.find(selectionFamiliar => selectionFamiliar.id === familiar.id)
                                    return markedFamiliar ?? familiar
                                })
                            }
                        }

                        return resident
                    })
                }
            }

            return center
        })

        onSelection(newSelection)
    }

    const handleCenterCheckboxChange = (option: ShareCenterOption) => {
        const newSelection = shareOptions?.map(centerShareOption => {
            if (centerShareOption.id === option.id) {
                return ShareOptionsUtils.copyCenterRecursive(
                    centerShareOption,
                    { checked: !centerShareOption.checked }
                )
            }
            return centerShareOption
        })

        onSelection(newSelection)
    }

    const handleResidentCheckboxChange = (option: ShareResidentOption) => {
        const newSelection = shareOptions?.map(centerShareOption => {
            if (centerShareOption.id === expandedCenterId) {
                return {
                    ...centerShareOption,
                    suboptions: centerShareOption.suboptions?.map(residentShareOption => {
                        if (residentShareOption.id === option.id) {
                            return ShareOptionsUtils.copyResidentRecursive(
                                residentShareOption,
                                { checked: !residentShareOption.checked }
                            )
                        }

                        return residentShareOption
                    })
                }
            }

            return centerShareOption
        })

        onSelection(newSelection)
    }

    const handleFamiliarCheckboxChange = (option: ShareFamiliarOption) => {
        const newSelection = shareOptions?.map(centerShareOption => {
            if (centerShareOption.id === expandedCenterId) {
                return {
                    ...centerShareOption,
                    suboptions: centerShareOption.suboptions?.map(residentShareOption => {
                        return {
                            ...residentShareOption,
                            suboptions: residentShareOption.suboptions?.map(familiarShareOption => {
                                if (familiarShareOption.id === option.id) {
                                    return ShareOptionsUtils.copyFamiliarRecursive(
                                        familiarShareOption,
                                        { checked: !familiarShareOption.checked }
                                    )
                                }

                                return familiarShareOption
                            })
                        }
                    })
                }
            }

            return centerShareOption
        })

        onSelection(newSelection)
    }

    const handleExpandShareCenterOption = (option: ShareCenterOption) => {
        setExpandedCenterId(option.id)
        setExpandedResidentId(null)
    }

    const handleExpandShareResidentOption = (option: ShareResidentOption) => {
        setExpandedResidentId(option.id)
    }

    const handleCancel = () => {
        onCancel()
    }

    const handleAccept = () => {
        onAccept()
    }

    return (
        <div className='publish-all-container'>
                <div className='publish-all-header'>
                    <div className='publish-all-title'>
                        {translate(t, 'common.TR_SHARE_WITH_LABEL')}
                    </div>
                    <div className='publish-all-search'>
                        <div className='search-container'>
                            <i className="fas fa-search search-icon"></i>
                            <input
                                type="text"
                                className="search-input"
                                placeholder={translate(t, 'common.TR_RECIPIENT_NAME_LABEL')}
                                value={searchQuery}
                                onChange={handleSearchChange}
                                disabled={loading}
                            />
                        </div>
                    </div>
                </div>
                <div className='publish-all-content'>
                    {loading 
                        ? (
                            <div className='loading-container'>
                                <Spinner loading size={40} />
                            </div>
                        )
                        : (
                            <div className='tables'>
                                <div className='table-container'>
                                    <div className='table-title'>
                                    <i
                                        onClick={markCenters}
                                        className={`fas toggle-icon fa-angle-up`}
                                    ></i>
                                        {translate(t, 'common.TR_ESTABLISHMENTS_LABEL')}
                                    </div>
                                    <div className='table'>
                                        {filteredCenters?.length === 0 ? (
                                            <div className="no-results">
                                                {translate(t, 'common.TR_NO_RESULTS_LABEL')}
                                            </div>
                                        ) : (
                                            filteredCenters?.map((center) => (
                                                <div
                                                    key={`center-${center.id}`}
                                                    className={`table-row ${expandedCenterId === center.id ? 'selected-row' : ''}`}
                                                    onClick={() => handleExpandShareCenterOption(center)}
                                                >
                                                    <input
                                                        type='checkbox'
                                                        className={`center-checkbox ${getCenterCheckType(center) === CheckType.partial ? 'partial' : ''}`}
                                                        value={center.id}
                                                        checked={getCenterCheckType(center) != null}
                                                        onChange={() => handleCenterCheckboxChange(center)}
                                                    />
                                                    <label className='checkbox-label'>
                                                        {center.name}
                                                    </label>
                                                    <i className='fa fa-chevron-right submenu-button'></i>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                </div>
                                {expandedCenterId ? (
                                    <div className='table-container'>
                                        <div className='table-title'>
                                        <i
                                            onClick={markResidents}
                                            className={`fas toggle-icon fa-angle-up`}
                                        ></i>
                                            {translate(t, 'common.TR_RESIDENTS_LABEL')}
                                        </div>
                                        <div className='table'>
                                            {filteredResidents?.length === 0 ? (
                                                <div className="no-results">
                                                    {translate(t, 'common.TR_NO_RESULTS_LABEL')}
                                                </div>
                                            ) : (
                                                filteredResidents?.map((residentShareOption) => (
                                                    <div
                                                        key={`center-${expandedCenterId}-resident-${residentShareOption.id}`}
                                                        className={`table-row ${expandedResidentId === residentShareOption.id ? 'selected-row' : ''}`}
                                                        onClick={() => handleExpandShareResidentOption(residentShareOption)}
                                                    >
                                                        <input
                                                            type='checkbox'
                                                            className={`center-checkbox ${getResidentCheckType(residentShareOption) === CheckType.partial ? 'partial' : ''}`}
                                                            value={residentShareOption.id}
                                                            checked={getResidentCheckType(residentShareOption) != null}
                                                            onChange={() => handleResidentCheckboxChange(residentShareOption)}
                                                            disabled={shareOptions
                                                                ?.find(center => center.id === expandedCenterId)?.checked}
                                                        />
                                                        <label className='checkbox-label'>
                                                            {residentShareOption.name}
                                                        </label>
                                                        <i
                                                            className='fa fa-chevron-right submenu-button'
                                                            onClick={() => handleExpandShareResidentOption(residentShareOption)}
                                                        ></i>
                                                    </div>
                                                ))
                                            )}
                                        </div>
                                    </div>
                                ) : null}
                                {expandedResidentId ? (
                                    <div className='table-container'>
                                        <div className='table-title'>
                                            <i
                                                onClick={markFamiliars}
                                                className={`fas toggle-icon fa-angle-up`}
                                            ></i>
                                            {translate(t, 'common.TR_FAMILIARS_LABEL')}
                                        </div>
                                        <div className='table'>
                                            {filteredFamiliars?.length === 0 ? (
                                                <div className="no-results">
                                                    {translate(t, 'common.TR_NO_RESULTS_LABEL')}
                                                </div>
                                            ) : (
                                                filteredFamiliars?.map((familiarShareOption) => (
                                                    <div
                                                        key={`center-${expandedCenterId}-resident-${expandedResidentId}-familiar-${familiarShareOption.id}`}
                                                        className='table-row'
                                                    >
                                                        <input
                                                            type='checkbox'
                                                            className='center-checkbox'
                                                            value={familiarShareOption.id}
                                                            checked={shareOptions
                                                                ?.some(center => center.suboptions
                                                                    ?.find(resident => resident.id === expandedResidentId)?.suboptions
                                                                    ?.some(familiar => familiar.id === familiarShareOption.id && familiar.checked)
                                                                )}
                                                            onChange={() => handleFamiliarCheckboxChange(familiarShareOption)}
                                                            disabled={filteredResidents
                                                                ?.find(resident => resident.id === expandedResidentId)?.checked}
                                                        />
                                                        <label className='checkbox-label'>
                                                            {familiarShareOption.name}
                                                        </label>
                                                    </div>
                                                ))
                                            )}
                                        </div>
                                    </div>
                                ) : null}
                            </div>
                    )}
                    <div className='publish-all-buttons'>
                        <button className='cancel-button' onClick={handleCancel}>
                            {translate(t, 'common.TR_CANCEL_LABEL')}
                        </button>
                        <button
                            className='accept-button'
                            onClick={handleAccept}
                            disabled={loading}
                        >
                            {translate(t, 'common.TR_CONFIRM_LABEL')}
                        </button>
                    </div>

                </div>

            </div>
    )
}